import { useState } from 'react';
import { useUserPayments } from '../hooks';
import { TableSkeleton } from '../components/LoadingSkeleton';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { RefundPaymentModal } from '../components/RefundPaymentModal';
import type { AdminPaymentRow } from '../types';

export function PaymentsTab({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  const [refundTarget, setRefundTarget] = useState<AdminPaymentRow | null>(null);
  const { data, isLoading, isError, error, refetch } = useUserPayments(userId, { page, limit: 20 });

  if (isLoading) return <TableSkeleton rows={5} cols={5} />;
  if (isError) return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  if (!data || data.items.length === 0)
    return <EmptyState title="No payments" hint="This user has not made any payments." />;

  return (
    <div className="space-y-3">
      <div className="glass-panel rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-surface-container-low text-[10px] uppercase tracking-widest text-on-surface-variant">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Gateway</th>
              <th className="px-3 py-2">Amount</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.id} className="border-t border-border-subtle">
                <td className="px-3 py-2 text-xs">{new Date(p.createdAt).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-xs">{p.gateway}</td>
                <td className="px-3 py-2">
                  {p.currency} {(p.amount / 100).toFixed(2)}
                  {p.refundedAt && (
                    <div className="text-[10px] text-red-300">
                      refunded {((p.refundAmount ?? 0) / 100).toFixed(2)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs uppercase">{p.status}</td>
                <td className="px-3 py-2 text-xs">{p.plan}</td>
                <td className="px-3 py-2 text-right">
                  {p.status === 'SUCCESS' && !p.refundedAt && (
                    <button
                      onClick={() => setRefundTarget(p)}
                      className="px-3 py-1 rounded-md text-[10px] uppercase tracking-widest font-bold border border-red-400/40 text-red-300 hover:bg-red-500/10"
                    >
                      Refund
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2 text-xs">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-md border border-border-subtle disabled:opacity-40">Prev</button>
        <span className="px-2 py-1.5 text-on-surface-variant">Page {page} / {data.meta.totalPages || 1}</span>
        <button disabled={page >= (data.meta.totalPages || 1)} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-md border border-border-subtle disabled:opacity-40">Next</button>
      </div>

      {refundTarget && (
        <RefundPaymentModal open={!!refundTarget} payment={refundTarget} onClose={() => setRefundTarget(null)} />
      )}
    </div>
  );
}
